import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { DENOMINATION_KEYS } from '../constants/nmpConstants';
var PICKER_OFFSET = 6;
var renderedDims = function (p) {
    var _a, _b, _c;
    var base = (_b = (_a = p.dimensions) !== null && _a !== void 0 ? _a : p.defaultDimensions) !== null && _b !== void 0 ? _b : { width: 55, height: 55 };
    var scale = (_c = p.scale) !== null && _c !== void 0 ? _c : 1;
    return { width: base.width * scale, height: base.height * scale };
};
var buildLabel = function (p, t) {
    var denomKey = DENOMINATION_KEYS[p.denomination];
    var denomName = denomKey ? t(denomKey) : p.denomination;
    if (p.type === 'moneyPiece') {
        var mp = p;
        return mp.pieceType === 'coins'
            ? t('text.moneyPieceCoinLabel', { denom: denomName })
            : t('text.moneyPieceLabel', { denom: denomName });
    }
    return t('text.numberPieceLabel', { denom: denomName });
};
var buildCandidates = function (manipulative, focusedPiece, t) {
    var _a;
    var sourceGroupId = focusedPiece.groupId;
    var others = manipulative
        .filter(function (m) {
        if (m.data.type !== 'numberPiece' && m.data.type !== 'moneyPiece')
            return false;
        var d = m.data;
        if (d.id === focusedPiece.id)
            return false;
        // Exclude pieces that are already in the source's group.
        if (sourceGroupId && d.groupId === sourceGroupId)
            return false;
        return true;
    })
        .map(function (m) { return m.data; });
    var groupMap = new Map();
    var standalones = [];
    for (var _i = 0, others_1 = others; _i < others_1.length; _i++) {
        var p = others_1[_i];
        if (p.groupId) {
            var arr = (_a = groupMap.get(p.groupId)) !== null && _a !== void 0 ? _a : [];
            arr.push(p);
            groupMap.set(p.groupId, arr);
        }
        else {
            standalones.push(p);
        }
    }
    // Stable group numbering matching the canvas order.
    var allGroupIds = [];
    manipulative.forEach(function (m) {
        var _a;
        var gId = (_a = m.data) === null || _a === void 0 ? void 0 : _a.groupId;
        var isNmp = m.data.type === 'numberPiece' || m.data.type === 'moneyPiece';
        if (isNmp && gId && !allGroupIds.includes(gId))
            allGroupIds.push(gId);
    });
    var candidates = [];
    groupMap.forEach(function (members, groupId) {
        candidates.push({
            kind: 'group',
            groupId: groupId,
            groupNumber: allGroupIds.indexOf(groupId) + 1,
            pieceIds: members.map(function (m) { return m.id; }),
            rects: members.map(function (m) {
                var _a = renderedDims(m), width = _a.width, height = _a.height;
                return {
                    left: m.center.x - width / 2,
                    top: m.center.y - height / 2,
                    width: width,
                    height: height,
                };
            }),
            labels: members.map(function (m) { return buildLabel(m, t); }),
        });
    });
    for (var _b = 0, standalones_1 = standalones; _b < standalones_1.length; _b++) {
        var p = standalones_1[_b];
        var _c = renderedDims(p), width = _c.width, height = _c.height;
        candidates.push({
            kind: 'standalone',
            pieceId: p.id,
            center: { x: p.center.x, y: p.center.y },
            width: width,
            height: height,
            label: buildLabel(p, t),
        });
    }
    return candidates;
};
var candidateBtnIds = function (c) {
    return c.kind === 'standalone' ? [c.pieceId] : c.pieceIds;
};
var NumberAndMoneyPieceGroupPicker = function () {
    var t = useTranslation().t;
    var manipulative = useContext(DataContext).manipulative;
    var _a = useState(null), focusedPieceId = _a[0], setFocusedPieceId = _a[1];
    var _b = useState(0), activeIdx = _b[0], setActiveIdx = _b[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    useEffect(function () {
        var onOpen = function (data) {
            returnFocusIdRef.current = data.focusedPieceId;
            setFocusedPieceId(data.focusedPieceId);
            setActiveIdx(0);
            announce(t('text.nmpGroupPickerOpened'));
        };
        customEventHandler.addEventListener('openNmpGroupPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openNmpGroupPicker', onOpen); };
    }, [t]);
    var focusedItem = focusedPieceId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedPieceId; })
        : undefined;
    var focusedPiece = focusedItem &&
        (focusedItem.data.type === 'numberPiece' || focusedItem.data.type === 'moneyPiece')
        ? focusedItem.data
        : null;
    var candidates = focusedPiece ? buildCandidates(manipulative, focusedPiece, t) : [];
    // Auto-close when source disappears or no candidates remain.
    useEffect(function () {
        if (focusedPieceId && (!focusedPiece || candidates.length === 0)) {
            setFocusedPieceId(null);
        }
    }, [focusedPieceId, focusedPiece, candidates.length]);
    // Click-outside closes; pointerdown is allowed to propagate so canvas drag
    // still works after the picker dismisses.
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var handleOutside = function (e) {
            var _a;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))
                return;
            setFocusedPieceId(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedPieceId]);
    // Move focus into the first candidate after the menu's own focus restore
    // settles (~50ms).
    useEffect(function () {
        if (!focusedPieceId)
            return;
        var id = setTimeout(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 60);
        return function () { return clearTimeout(id); };
    }, [focusedPieceId]);
    if (!focusedPieceId || !focusedPiece || candidates.length === 0)
        return null;
    var closePicker = function () {
        setFocusedPieceId(null);
        var id = returnFocusIdRef.current;
        if (id) {
            requestAnimationFrame(function () {
                var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                el === null || el === void 0 ? void 0 : el.focus();
            });
        }
    };
    var onSelect = function (candidate) {
        var _a;
        // When the source is already grouped, pass its groupId so the merge
        // pulls every member of the source group along with it. The
        // groupDiceFromMenu handler resolves group ids to their members.
        var sourceId = (_a = focusedPiece.groupId) !== null && _a !== void 0 ? _a : focusedPieceId;
        var diceIds = candidate.kind === 'standalone'
            ? [sourceId, candidate.pieceId]
            : [candidate.groupId, sourceId];
        customEventHandler.dispatchEvent('bottomPanel', {
            id: 'groupDiceFromMenu',
            panel: 'bottomPanel',
            diceIds: diceIds,
        });
        setFocusedPieceId(null);
    };
    var moveCandidateFocus = function (delta) {
        var next = (activeIdx + delta + candidates.length) % candidates.length;
        setActiveIdx(next);
        var cand = candidates[next];
        var targetId = cand.kind === 'standalone' ? cand.pieceId : cand.pieceIds[0];
        requestAnimationFrame(function () {
            var _a;
            var btn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.nmpGroupPicker__target[data-btn-id=\"".concat(targetId, "\"]"));
            btn === null || btn === void 0 ? void 0 : btn.focus();
        });
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowDown' ||
            (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(1);
        }
        else if (e.key === 'ArrowLeft' ||
            e.key === 'ArrowUp' ||
            (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveCandidateFocus(-1);
        }
    };
    var getCandidateIdx = function (btnId) {
        return candidates.findIndex(function (c) { return candidateBtnIds(c).includes(btnId); });
    };
    var onBtnFocus = function (btnId) {
        var idx = getCandidateIdx(btnId);
        if (idx !== -1)
            setActiveIdx(idx);
    };
    var btnCounter = 0;
    return (_jsx("div", { ref: containerRef, className: "nmpGroupPicker", role: "dialog", "aria-modal": "true", "aria-label": t('text.nmpGroupPickerLabel'), onKeyDown: onContainerKeyDown, children: candidates.map(function (cand, candIdx) {
            var isActive = candIdx === activeIdx;
            var ids = candidateBtnIds(cand);
            return ids.map(function (pieceId, btnIdx) {
                var isFirstOverall = btnCounter === 0;
                var isFirstInCand = btnIdx === 0;
                btnCounter++;
                var left;
                var top;
                var width;
                var height;
                if (cand.kind === 'standalone') {
                    left = cand.center.x - cand.width / 2;
                    top = cand.center.y - cand.height / 2;
                    width = cand.width;
                    height = cand.height;
                }
                else {
                    var r = cand.rects[btnIdx];
                    left = r.left;
                    top = r.top;
                    width = r.width;
                    height = r.height;
                }
                var isSrVisible = isFirstInCand;
                var ariaLabel = isSrVisible
                    ? cand.kind === 'standalone'
                        ? t('text.nmpGroupPickerStandaloneTarget', { piece: cand.label })
                        : t('text.nmpGroupPickerGroupTarget', {
                            number: cand.groupNumber,
                            count: cand.pieceIds.length,
                            pieces: cand.labels.join(', '),
                        })
                    : undefined;
                return (_jsx("button", { ref: isFirstOverall ? firstBtnRef : undefined, type: "button", className: "nmpGroupPicker__target".concat(isActive ? ' nmpGroupPicker__target--active' : ''), "data-btn-id": pieceId, "data-cand-idx": candIdx, tabIndex: isSrVisible ? 0 : -1, "aria-hidden": isSrVisible ? undefined : true, style: {
                        position: 'absolute',
                        left: left - PICKER_OFFSET,
                        top: top - PICKER_OFFSET,
                        width: width + PICKER_OFFSET * 2,
                        height: height + PICKER_OFFSET * 2,
                        zIndex: 99999,
                    }, "aria-label": ariaLabel, onFocus: function () { return isSrVisible && onBtnFocus(pieceId); }, onClick: function () { return onSelect(cand); } }, "".concat(pieceId, "-").concat(cand.kind)));
            });
        }) }));
};
export default NumberAndMoneyPieceGroupPicker;
